<template>
  <section>
    <!-- top -->
    <div class="mt-5 px-2 flex items-center gap-x-8">
      <template v-if="isCreateMode">
        <link-icon />
        <div>
          <h4 class="font-semibold text-root-2">Link Information</h4>
          <p class="text-root-5 text-xs font-light">
            Tell us more about your payment link
          </p>
        </div>
      </template>
      <template v-if="isEditMode">
        <div class="w-full">
          <p
            class="text-root-5 text-xs font-light text-center italic flex items-center justify-center w-full"
          >
            Input Details
          </p>
        </div>
      </template>
    </div>
    <!-- main form -->
    <div class="mt-5">
      <el-form
        :model="createPaymentLinkForm"
        :rules="rules"
        label-position="top"
        ref="createPaymentLinkForm"
      >
        <el-form-item label="Payment Link Title" prop="title">
          <el-input
            v-model="createPaymentLinkForm.title"
            placeholder="Enter desired link name"
          />
        </el-form-item>
        <el-form-item label="Payment Link Description" prop="description">
          <el-input
            v-model="createPaymentLinkForm.description"
            placeholder="Enter desired link description"
          />
        </el-form-item>
        <el-form-item label="Amount To Receive ($)" prop="amount">
          <el-input
            v-model.number="createPaymentLinkForm.amount"
            placeholder="$ Enter Amount"
          />
        </el-form-item>
        <el-form-item label="Custom Message" prop="message">
          <el-input
            v-model="createPaymentLinkForm.message"
            type="textarea"
            :autosize="{ minRows: 4, maxRows: 8 }"
            placeholder="Type Message"
          />
        </el-form-item>
        <!-- conditionally show Additional fields -->
        <div
          v-for="(field, index) in extraFields"
          :key="index"
          class="relative"
        >
          <el-form-item label="Additional Field" prop="">
            <el-input v-model="field.name" placeholder="Enter Field Name" />
            <el-button
              icon="el-icon-close"
              class="absolute right-4 top-2 rounded-full outline-none border-none"
              @click="removeFieldHandler(field.id)"
            ></el-button>
          </el-form-item>
        </div>
        <!-- add another field -->
        <div class="flex items-center justify-center my-8">
          <el-button
            class="rounded-full text-xs"
            icon="el-icon-circle-plus"
            @click="addAnotherFieldHandler"
            >Add Another Field</el-button
          >
          <el-tooltip placement="top" effect="light">
            <div slot="content" class="px-6 py-0.5">
              <h3 class="font-semibold my-2 border-b pb-2">
                Add Another Field
              </h3>
              <div class="border rounded bg-pink-50 px-4 py-2">
                <h4 class="font-semibold my-4">Additional Information</h4>
                <p class="pb-4">
                  Need extra information from your customers?<br />
                  Additional field helps you to get more information<br />
                  to create the payment link.
                </p>
              </div>
            </div>
            <el-button
              icon="el-icon-info"
              class="rounded-full outline-none bg-transparent border-none"
            ></el-button>
          </el-tooltip>
        </div>
        <el-form-item label="" prop="feeBearer">
          <div class="flex items-center justify-between mt-4">
            <p class="font-light text-sm flex flex-col">
              <span class="font-semibold"
                >Charge Fee on Customer's Payment</span
              >
              <span class="text-xs"
                >Transaction fee will be added to your customer's bill</span
              >
            </p>
            <el-switch
              active-value="customer"
              inactive-value="business"
              v-model="createPaymentLinkForm.feeBearer"
            ></el-switch>
          </div>
        </el-form-item>
        <el-form-item label="" prop="collectAddress">
          <div class="flex items-center justify-between mt-4">
            <p class="font-light text-sm flex flex-col">
              <span class="font-semibold">Collect Customer Address</span>
              <span class="text-xs">Collect Customer Address</span>
            </p>
            <el-switch
              v-model="createPaymentLinkForm.collectAddress"
            ></el-switch>
          </div>
        </el-form-item>
        <el-form-item label="" prop="collectPhoneNumber">
          <div class="flex items-center justify-between mt-4">
            <p class="font-light text-sm flex flex-col">
              <span class="font-semibold">Collect Customer's Phone Number</span>
              <span class="text-xs">Collect Customer's Phone Number</span>
            </p>
            <el-switch
              v-model="createPaymentLinkForm.collectPhoneNumber"
            ></el-switch>
          </div>
        </el-form-item>
        <el-form-item label="" prop="type">
          <div class="flex items-center justify-between mt-4">
            <p class="font-light text-sm flex flex-col">
              <span class="font-semibold">One Time Payment</span>
              <span class="text-xs"
                >Disable this link immediately a customer uses it</span
              >
            </p>
            <el-switch
              active-value="one_time"
              inactive-value="recurring"
              v-model="createPaymentLinkForm.type"
            ></el-switch>
          </div>
        </el-form-item>
        <el-form-item>
          <o-button
            v-if="isCreateMode"
            :loading="isLoading"
            :disabled="isLoading"
            @click="submitForm('createPaymentLinkForm')"
            class="bg-primary text-white w-full rounded-3xl outline-none border-none mt-7"
            >Continue</o-button
          >
          <div v-if="isEditMode">
            <div class="flex items-center gap-x-1.5">
              <el-button
                v-if="isRoutePaymentLink"
                @click="goBack()"
                class="bg-white border-primary border text-primary rounded-3xl mt-5 shadow-sm w-full px-8 md:px-12 py-3.5 md:py-4"
                >Cancel</el-button
              >
              <el-button
                :loading="loading"
                @click="submitForm('createPaymentLinkForm')"
                class="bg-primary text-white rounded-3xl mt-5 shadow-sm w-full px-8 md:px-12 py-3.5 md:py-4"
                >{{ !loading ? 'Update Link' : 'Updating Link' }}</el-button
              >
            </div>
          </div>
        </el-form-item>
      </el-form>
    </div>
  </section>
</template>

<script>
import { FormatAmount } from '@/utils/helpers'

export default {
  props: {
    mode: {
      type: String,
      default: '',
      required: true
    }
  },
  components: {
    LinkIcon: () => import('@/assets/icons/LinkIcon.vue')
  },
  data () {
    return {
      isLoading: false,
      loading: false,
      createPaymentLinkForm: {
        title: '',
        description: '',
        amount: '',
        message: '',
        type: 'one_time',
        collectPhoneNumber: false,
        collectAddress: false,
        feeBearer: 'business'
      },
      extraFields: [],
      rules: {
        title: [
          {
            required: true,
            message: 'Please enter link title',
            trigger: 'blur'
          }
        ],
        description: [
          {
            required: true,
            message: 'Please enter link description',
            trigger: 'blur'
          }
        ],
        amount: [
          {
            type: 'number',
            required: true,
            message: 'Enter amount',
            trigger: 'blur'
          }
        ]
      }
    }
  },
  computed: {
    routeName () {
      return this.$route.name
    },
    isRoutePaymentLink () {
      return this.routeName === 'Payment Link'
    },
    isCreateMode () {
      return this.mode === 'create'
    },
    paymentDetails () {
      return this.$store.state.paymentLink.currentPaymentLinkDetails
    },
    isEditMode () {
      return this.mode === 'edit'
    }
  },
  methods: {
    validateAmount (rule, value, callback) {
      if (!FormatAmount(value) || FormatAmount(value) < 1) {
        callback(new Error('Provide an amount'))
      } else {
        callback()
      }
    },
    submitForm (formName) {
      this.$refs[formName].validate((valid) => {
        if (valid) {
          const extraFields = this.extraFields.map((field) => {
            if (field.name !== '') {
              return field.name
            }
          })
          const payload = { ...this.createPaymentLinkForm, extraFields }
          if (this.isEditMode) {
            this.updatePaymentDetails(payload)
          } else {
            this.createPaymentLink(payload)
          }
        } else {
          this.$notify({
            showClose: true,
            message: 'Please fill all inputs correctly',
            type: 'error'
          })
        }
      })
    },
    goBack () {
      this.$router.go(-1)
    },
    addAnotherFieldHandler () {
      this.extraFields.push({
        name: '',
        id: this.extraFields.length
      })
    },
    populateExtraFields () {
      const extras = this.paymentDetails.extraFields
      for (let i = 0; i < extras.length; i++) {
        this.extraFields.push({
          name: extras[i],
          id: i
        })
      }
    },
    updatePaymentDetails (payload) {
      const id = this.paymentDetails.id
      this.loading = true
      const vm = this
      this.$store
        .dispatch('paymentLink/updatePaymentLink', { payload, id })
        .then(() => {
          this.loading = false
          this.$emit('successful', 'updated')
        })
        .catch((error) => {
          const err = error.response?.data
            ? error.response.data.error.publicMessage
            : error
          vm.$message({
            showClose: true,
            message: this.httpErrorMessage,
            type: 'error'
          })
        })
        .finally(() => {
          this.loading = false
        })
    },
    removeFieldHandler (id) {
      this.extraFields = this.extraFields.filter((field) => field.id !== id)
    },
    createPaymentLink (payload) {
      this.isLoading = true
      const vm = this
      this.$store
        .dispatch('paymentLink/createPaymentLink', payload)
        .then(() => {
          this.isLoading = false
          this.resetField('createPaymentLinkForm')
          this.$emit('successful')
        })
        .catch((error) => {
          const err = error.response ? error.response.data : error
          vm.$message({
            showClose: true,
            message: this.httpErrorMessage,
            type: 'error'
          })
        })
        .finally(() => {
          this.isLoading = false
        })
    },
    resetField (form) {
      this.$refs[form].resetFields()
    }
  },
  created () {
    if (this.paymentDetails && this.isEditMode) {
      this.createPaymentLinkForm.amount = this.paymentDetails.amount.amount
      this.createPaymentLinkForm.description = this.paymentDetails.description
      this.createPaymentLinkForm.title = this.paymentDetails.title
      this.createPaymentLinkForm.message = this.paymentDetails.message
      this.createPaymentLinkForm.type = this.paymentDetails.type
      this.createPaymentLinkForm.collectPhoneNumber =
        this.paymentDetails.collectPhoneNumber
      this.createPaymentLinkForm.collectAddress =
        this.paymentDetails.collectAddress
      this.createPaymentLinkForm.feeBearer = this.paymentDetails.feeBearer
      this.populateExtraFields()
    }
  },
  // watch: {
  //   'createPaymentLinkForm.amount': function (newValue) {
  //     let result = newValue.replace(/\D/g, '')
  //     result = AddComma(result)
  //     result.replace(/^0+/g, '')
  //     this.$nextTick(() => {
  //       this.createPaymentLinkForm.amount = ` $${result}`
  //     })
  //   },
  //   deep: true
  // },
  destroyed () {
    this.$store.dispatch('paymentLink/setCurrentPaymentLinkDetails', null)
  }
}
</script>

<style scoped>
>>> .el-form-item {
  margin-bottom: 0.7rem;
}
>>> .el-form-item__label {
  font-size: 0.8rem;
  margin-bottom: -0.35rem;
  padding: 0;
}
>>> .el-switch.is-checked .el-switch__core {
  border-color: var(--color-primary);
  background-color: var(--color-primary);
}
>>> .el-switch.is-checked .el-switch__core {
  border-color: var(--color-primary);
  background-color: var(--color-primary);
}
>>> .el-input__inner {
  border-radius: 8px;
  border-color: #c7c8d2;
  height: 48px;
}
</style>
